/**
 * <technical-data-slider>
 *
 * Initialises a Swiper for the technical data drawings slider, plus a
 * GLightbox gallery scoped to this element so clicking any slide image
 * opens a full-size lightbox with prev/next navigation through all the
 * technical data images.
 * Stays active on all viewport sizes.
 */
class TechnicalDataSlider extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {any} */
  #lightbox = null;

  connectedCallback() {
    this.#init();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this)) {
        this.#destroy();
        this.#init();
      }
    }, { once: true });
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #destroy() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#lightbox?.destroy();
    this.#lightbox = null;
  }

  #init() {
    const swiperEl = this.querySelector('.td-section__swiper');
    if (!swiperEl) return;

    const prevBtn = this.querySelector('.td-section__nav-btn--prev');
    const nextBtn = this.querySelector('.td-section__nav-btn--next');
    const scrollbar = this.querySelector('.td-section__scrollbar');

    this.#swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
        disabledClass: 'swiper-button-disabled',
      },
      scrollbar: {
        el: scrollbar,
        draggable: true,
        hide: false,
      },
    });

    this.#initLightbox();
  }

  #initLightbox() {
    const Lightbox = /** @type {any} */ (window).GLightbox;
    if (typeof Lightbox !== 'function') {
      // GLightbox script may still be loading — retry once it is ready.
      const script = document.querySelector('script[src*="glightbox"]');
      if (script) {
        script.addEventListener('load', () => this.#initLightbox(), { once: true });
      }
      return;
    }

    if (!this.id) return;

    const selector = `#${this.id} a.glightbox`;
    if (!document.querySelector(selector)) return;

    this.#lightbox = Lightbox({
      selector,
      touchNavigation: true,
      keyboardNavigation: true,
      loop: true,
      zoomable: true,
      draggable: true,
      openEffect: 'fade',
      closeEffect: 'fade',
      slideEffect: 'slide',
    });
  }
}

customElements.define('technical-data-slider', TechnicalDataSlider);
